import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../App";
import Header from "../components/Header";
import { api, extractApiErrorMessage } from "../services/apiClient";
import { MICROCOPY } from "../constants/microcopy";
import { getResumeActivationCta } from "../constants/activationResume";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const resumeActivationCta = getResumeActivationCta({
    isAuthenticated: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/auth/login", {
        ...formData,
        email: formData.email.trim().toLowerCase(),
      });

      const responseText = await response.text();
      let data = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(data, "Error al iniciar sesión"),
        );
      }

      login(data.access_token, data.user);
      toast.success("¡Bienvenido!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error?.message || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center p-8 md:p-12"
        >
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1
                className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                data-testid="login-title"
              >
                Iniciar Sesión
              </h1>
              <p className="text-muted-foreground">
                {MICROCOPY.auth.login.subtitle}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-muted/20 p-4 mb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                {resumeActivationCta.eyebrow}
              </p>
              <p className="text-sm font-medium">
                {resumeActivationCta.title}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {resumeActivationCta.description}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
              data-testid="login-form"
            >
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
                <div className="mt-2 text-right">
                  <Link
                    to="/reset-password"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              ¿No tienes cuenta?{" "}
              <Link
                to="/register"
                className="font-medium text-primary hover:underline"
              >
                Regístrate
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block relative overflow-hidden"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-secondary/80"></div>
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-12">
            <div className="text-center text-primary-foreground">
              <h2
                className="text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                {MICROCOPY.auth.login.sideTitle}
              </h2>
              <p className="text-xl opacity-90">
                {MICROCOPY.auth.login.sideDescription}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
